import { setViewModel } from './finderSlice';
import { setState as setAppState } from '../appState/appStateSlice';
import { AnyAction } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import i18n from '../../i18n/config';

import { NavigateParams } from '../../components/BtNavigate';
import { AppDispatch, RootState, store } from '../../app/store';
import { api, apiPostV2, Failure, getBaseUrl } from '../../support/apiCallHelper';
import { COMPANY, CONTRACT_CUSTOMER, USER, VEHICLE } from '../../support/apiConstants';
import { CheckboxParams } from '../../components/BtCheckbox';
import { ButtonParams } from '../../components/BtButton';
import {
  Company,
  Customer,
  NoCompany,
  NoCustomer,
  NoPerson,
  NoVehicle,
  NoWeight,
  Person,
  Vehicle,
  Weight,
} from '../../components/BtGroup';
import { FailedParams } from '../../components/BtFailed';
import { LoadingParams } from '../../components/BtLoading';
import { createGatMission, createGatSelfServe } from '../gat/gatApi';
import { setSessionData } from '../gat/gatSessionSlice';
import { useSelector } from 'react-redux';

export function none(): FinderScreen.None {
  return {
    type: 'none',
    texts: {
      title: '',
      back: '',
      home: '',
      next: '',
      titleUseAddress: '',
      useAddress: '',
      noMatch: '',
      weight: '',
      metaTitle: '',
      loading: '',
      noMatchCustomer: '',
      matchCustomer: '',
    },
    state: {
      showUseAddress: false,
      useAddress: true,
      items: [null],
    },
    error: {
      status: '',
      body: '',
    },
    loading: {
      title: '',
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: false },
        image: '',
      },
    },
  };
}

export namespace FinderScreen {
  interface Texts {
    title: string;
    back: string;
    home: string;
    next: string;
    weight: string;
    titleUseAddress: string;
    useAddress: string;
    noMatch: string;
    metaTitle: string;
    loading: string;
    noMatchCustomer: string;
    matchCustomer: string;
  }

  export interface State {
    showUseAddress: boolean;
    useAddress: boolean;
    items: [
      | Vehicle
      | NoVehicle
      | Weight
      | NoWeight
      | Person
      | NoPerson
      | Company
      | NoCompany
      | Customer
      | NoCustomer
      | null,
    ];
  }

  export interface None {
    type: 'none';
    texts: Texts;
    state: State;
    error: FailedParams;
    loading: LoadingParams;
  }

  export interface Loading {
    type: 'loading';
    texts: Texts;
    state: State;
    loading: LoadingParams;
  }

  export interface Ready {
    type: 'ready';
    texts: Texts;
    state: State;
    greeting: string;
    next: ButtonParams;
    titleUseAddress: string;
    navigation: NavigateParams;
    useAddress: CheckboxParams;
    items: [
      | Vehicle
      | NoVehicle
      | Weight
      | NoWeight
      | Person
      | NoPerson
      | Company
      | NoCompany
      | Customer
      | NoCustomer
      | null,
    ];
    metaTitle: string;
  }

  export interface Failed {
    type: 'failed';
    texts: Texts;
    state: State;
    error: FailedParams;
  }

  export type ViewModel = None | Loading | Ready | Failed;

  export function isNone(viewModel: ViewModel): viewModel is None {
    return (viewModel as None).type === 'none';
  }
  export function isLoading(viewModel: ViewModel): viewModel is Loading {
    return (viewModel as Loading).type === 'loading';
  }
  export function isReady(viewModel: ViewModel): viewModel is Ready {
    return (viewModel as Ready).type === 'ready';
  }
  export function isFailed(viewModel: ViewModel): viewModel is Failed {
    return (viewModel as Failed).type === 'failed';
  }

  function getTexts(viewModel: ViewModel): Texts {
    if (isNone(viewModel)) return viewModel.texts;
    if (isLoading(viewModel)) return viewModel.texts;
    if (isReady(viewModel)) return viewModel.texts;
    if (isFailed(viewModel)) return viewModel.texts;
    return {
      title: '',
      home: '',
      back: '',
      next: '',
      titleUseAddress: '',
      useAddress: '',
      noMatch: '',
      weight: '',
      metaTitle: '',
      loading: '',
      noMatchCustomer: '',
      matchCustomer: '',
    };
  }

  function loading(viewModel: ViewModel, state: State, logoUrl: string): Loading {
    const t = i18n.t;

    return {
      type: 'loading',
      texts: {
        title: t('finderScreen.title'),
        back: t('finderScreen.back'),
        home: t('finderScreen.home'),
        next: t('finderScreen.next'),
        useAddress: t('finderScreen.useAddress'),
        titleUseAddress: t('finderScreen.titleUseAddress'),
        noMatch: t('finderScreen.noMatch'),
        weight: t('finderScreen.weight'),
        metaTitle: t('finderScreen.metaTitle'),
        loading: t('loading.title'),
        noMatchCustomer: t('finderScreen.noMatchCustomer'),
        matchCustomer: t('finderScreen.matchCustomer'),
      },
      loading: {
        title: t('loading.title'),
        navigation: {
          back: { title: '', image: 'ico_back_arrow', visible: false },
          home: { title: '', image: 'ico_home', visible: false },
          image: logoUrl,
        },
      },
      state: state,
    };
  }

  function failed(
    viewModel: ViewModel,
    texts: Texts,
    state: State,
    body: string,
    status: string,
  ): Failed {
    return {
      type: 'failed',
      texts: texts,
      state: state,
      error: {
        status: status,
        body: body,
      },
    };
  }

  function ready(viewModel: ViewModel, state: State, logoUrl: string): Ready {
    const texts = getTexts(viewModel);

    return {
      type: 'ready',
      texts: texts,
      greeting: texts.title,
      next: { title: texts.next, image: null, visible: true },
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: true },
        home: { title: '', image: 'ico_home', visible: true },
        image: logoUrl,
      },
      titleUseAddress: texts.titleUseAddress,
      useAddress: {
        id: 'useAddress',
        title: texts.useAddress,
        message: '',
        value: state.useAddress,
        visible: state.showUseAddress,
      },
      items: state.items,
      metaTitle: texts.metaTitle,
      state: state,
    };
  }

  export function start(
    viewModel: ViewModel,
    logoUrl: string,
  ): (dispatch: Dispatch<AnyAction>) => void {
    const t = i18n.t;
    const appState = store.getState().appState.state;

    if (!appState.sessionActive) {
      return (dispatch) => {
        dispatch(
          setViewModel(
            failed(viewModel, viewModel.texts, viewModel.state, t('failed.session'), ''),
          ),
        );
      };
    }

    return (dispatch) => {
      dispatch(setViewModel(loading(viewModel, viewModel.state, logoUrl)));
    };
  }

  export async function reset(): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(setViewModel(none()));
    };
  }

  export interface VehicleResult {
    registrationNumber: string;
    make: string;
    brand: string;
    model: string;
    years: number;
  }
  export interface CustomerLookupResponse {
    firstName: string;
    lastName: string;
    address: string;
    zipCode: string;
    city: string;
  }

  interface ContractCustomerResult {
    contractCode: string;
    email: string;
    name: string;
    organisationNumber: string;
    phone: string;
  }

  export function load(viewModel: ViewModel, logoUrl: string) {
    return async (dispatch: AppDispatch) => {
      const controller = new AbortController();
      const newState: [
        | Vehicle
        | NoVehicle
        | Weight
        | NoWeight
        | Person
        | NoPerson
        | Company
        | NoCompany
        | Customer
        | NoCustomer
        | null,
      ] = [null];

      const appState = store.getState().appState.state;

      const sessionMissionId = store.getState().gatSession.missionId;
      const sessionAccessToken = store.getState().gatSession.accessToken;
      const sessionToken = store.getState().gatSession.token;

      /* const localStorageMissionId = localStorage.getItem('missionId');
      const localStorageAccessToken = localStorage.getItem('accessToken');
      const localStorageToken = localStorage.getItem('token');*/

      if (!sessionMissionId || !sessionAccessToken || !sessionToken) {
        await dispatch(createGatMission());
      } else {
        dispatch(
          setSessionData({
            missionId: sessionMissionId,
            accessToken: sessionAccessToken,
            token: sessionToken,
          }),
        );
      }

      const { missionId, token } = store.getState().gatSession;

      let showUseAddress = false;
      let city = '';
      let street = '';
      let zipCode = '';
      let customerName = '';
      let vechicleType = '';
      const regno = appState.regno;
      const govId = appState.govId;
      const govNo = appState.govNo;
      let customerNumber = appState.customerNumber;

      const result = await dispatch(
        createGatSelfServe({
          missionId,
          token,
          customerType: appState.isActive.charAt(0).toUpperCase() + appState.isActive.slice(1),
          customerCode: govId || govNo,
          registrationNumber: regno,
        }),
      );

      if (createGatSelfServe.fulfilled.match(result)) {
        const response = result.payload;

        if (response instanceof Failure) {
          newState.push({
            type: 'noCustomer',
            icon: 'ico_error',
            message: viewModel.texts.noMatch,
          });
          return newState;
        }

        if (appState.registered) {
          const vehicleData = response.customerVehicle;
          if (vehicleData?.make || vehicleData?.model) {
            const make = vehicleData.make ? `${vehicleData.make}, ` : '';
            const model = vehicleData.model ?? '';
            newState.push({
              type: 'vehicle',
              icon: 'ico_vehicle_info',
              model: make + model,
              regno: vehicleData.registrationNumber ?? '',
            });
            vechicleType = make + model;
          } else {
            newState.push({
              type: 'noVehicle',
              icon: 'ico_vehicle_no_info',
              message: viewModel.texts.noMatch,
            });
          }

          /*   if (appState.weight?.length > 0) {
            newState.push({
              type: 'weight',
              icon: 'ico_vehicle_info_weight',
              weight: `${appState.weight} ${viewModel.texts.weight}`,
            });
          } else {
            newState.push({
              type: 'noWeight',
              icon: 'ico_vehicle_info_weight',
              message: viewModel.texts.noMatch,
            });
          }*/

          const customer = response.customer;
          const isPerson = appState.isActive === 'person';
          const isCompany = appState.isActive === 'company';

          if (customer) {
            const { firstName, lastName, address, zipCode: zip, city: customerCity } = customer;

            if (isPerson && firstName && lastName && zip) {
              newState.push({
                type: 'person',
                icon: 'ico_person_info',
                firstName,
                lastName,
                street: address,
                zipCode: zip,
                city: customerCity,
              });
              showUseAddress = true;
              street = address;
              zipCode = zip;
              city = customerCity;
              customerName = `${firstName} ${lastName}`;
            } else if (isCompany && firstName && address) {
              newState.push({
                type: 'company',
                icon: 'ico_company_info',
                name: firstName,
                street: address,
                zipCode: zip,
                city: customerCity,
              });
              showUseAddress = true;
              street = address;
              zipCode = zip;
              city = customerCity;
              customerName = firstName;
            } else {
              newState.push({
                type: isPerson ? 'noPerson' : 'noCompany',
                icon: isPerson ? 'ico_person_no_info' : 'ico_company_no_info',
                message: viewModel.texts.noMatch,
              });
            }
          }

          if (govNo !== '' && customerNumber) {
            const callContractCustomer = await api<ContractCustomerResult>(
              CONTRACT_CUSTOMER,
              `?contractCode=${customerNumber}&organisationNumber=${govNo.replaceAll('-', '')}`,
              controller,
            );
            if (callContractCustomer instanceof Failure) {
              const noCustomer: NoCustomer = {
                type: 'noCustomer',
                icon: 'ico_customer_no_info',
                message: viewModel.texts.noMatchCustomer,
              };
              newState.push(noCustomer);
              customerNumber = '';
            } else {
              const contractCustomer = callContractCustomer as ContractCustomerResult;

              if (contractCustomer.contractCode) {
                const customer: Customer = {
                  type: 'customer',
                  icon: 'ico_customer_info',
                  number: contractCustomer.contractCode,
                  message: viewModel.texts.matchCustomer,
                };
                newState.push(customer);
              } else {
                const noCustomer: NoCustomer = {
                  type: 'noCustomer',
                  icon: 'ico_customer_no_info',
                  message: viewModel.texts.noMatchCustomer,
                };
                newState.push(noCustomer);
                customerNumber = '';
              }
            }
          }
        }
      }

      dispatch(
        setAppState({
          ...appState,
          address: street,
          zipCode: zipCode,
          city: city,
          vehicleType: vechicleType,
          customerName: customerName,
          regno: regno,
          govNo: govNo,
          govId: govId,
          customerNumber: customerNumber,
        }),
      );

      dispatch(
        setViewModel(
          ready(
            viewModel,
            {
              ...viewModel.state,
              items: newState,
              showUseAddress: showUseAddress,
            },
            logoUrl,
          ),
        ),
      );
    };
  }

  export function startAddress(
    viewModel: ViewModel,
    useAddress: boolean,
    logoUrl: string,
  ): (dispatch: Dispatch<AnyAction>) => void {
    const appState = store.getState().appState.state;
    let city: string;
    let street: string;
    let zipCode: string;

    viewModel.state.items.forEach((Item) => {
      if (Item?.type === 'company' || Item?.type === 'person') {
        street = Item.street;
        zipCode = Item.zipCode;
        city = Item.city;
      }
    });

    return (dispatch) => {
      dispatch(
        setViewModel(ready(viewModel, { ...viewModel.state, useAddress: useAddress }, logoUrl)),
      );

      if (useAddress) {
        dispatch(setAppState({ ...appState, address: street, zipCode: zipCode, city: city }));
      } else {
        dispatch(setAppState({ ...appState, address: '', zipCode: '', city: '' }));
      }
    };
  }
}
